import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Grid from '@mui/material/Grid';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import { openNotification } from 'components';
import type { QueryKey } from '@tanstack/react-query';
interface EntityCreationModalProps<T, R = unknown, P = Record<string, never>> {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (payload: { data: R; formData: T }) => void;
  title: string;
  description: string;
  cancelText: string;
  saveText: string;
  FormComponent: React.ComponentType<FormProps<T> & P>;
  initialFormData: T;
  mutationFn: (data: T, options?: { onSuccess?: (data: R) => void }) => Promise<R>;
  invalidateQueries?: QueryKey[];
  successNotification?: {
    title: string;
    description: string;
  };
  errorNotification?: {
    title: string;
    description: string;
  };
  formProps?: P;
}

interface FormProps<T> {
  onValidityChange: (isValid: boolean) => void;
  onFormDataChange: (data: T) => void;
  initialValues?: T;
}

export function PyEntityCreationModal<T, R = unknown, P = Record<string, never>>({
  isOpen,
  onClose,
  onSuccess,
  title,
  description,
  cancelText,
  saveText,
  FormComponent,
  initialFormData,
  mutationFn,
  invalidateQueries = [],
  successNotification,
  errorNotification,
  formProps = {} as P,
}: EntityCreationModalProps<T, R, P>) {
  const [canSave, setCanSave] = useState(false);
  const [formData, setFormData] = useState<T>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await mutationFn(formData, {
        onSuccess: (data) => {
          invalidateQueries.forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
          });

          if (successNotification) {
            openNotification({
              title: successNotification.title,
              description: successNotification.description,
              type: 'success',
            });
          }

          onClose();
          onSuccess?.({ data, formData });
        },
      });
    } catch (error) {
      onClose();
      trackError(error instanceof Error ? error : new Error(String(error)));

      if (errorNotification) {
        openNotification({
          title: errorNotification.title,
          description: errorNotification.description,
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth sx={{ minHeight: 540 }}>
      <DialogTitle>{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: '24px',
          top: '20px',
          color: theme.palette.action.active,
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <FormComponent onValidityChange={setCanSave} onFormDataChange={setFormData} initialValues={initialFormData} {...formProps} />
      </DialogContent>

      <DialogActions sx={{ my: 2, mr: 2 }}>
        <Grid item xs={12} gap={2}>
          <Button variant="text" color="primary" onClick={onClose}>
            {cancelText}
          </Button>
          <Button variant="contained" color="primary" disabled={!canSave || isLoading} onClick={handleSave}>
            {saveText}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
